import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { exhibitionColumnsVisibility } from "@/config/dashboard/exhibition";
import { FormLayoutElement } from "@/config/forms/general";
import { tiragesColumnsVisibility } from "@/config/dashboard/tirages";
import { Exhibition, Tirage, TirageInExhibition } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  ExhibitionLinkGridCard,
  TirageLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import TirageInExhibitionDrawer from "./drawer/TirageInExhibitionDrawer";
import { ComplexItinerantGate } from "./drawer/ComplexItinerantGate";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage | undefined;
  exhibition: Exhibition | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * TirageInExhibitions component
 * @returns {JSX.Element}
 */
const TirageInExhibitions = ({
  id,
  tirage,
  exhibition,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: complexeRelation } = useGetComplexRelationFields(
    id,
    "tirageInExhibition",
    type,
  );

  const { mutate: deleteTirageInExhibition } = useDeleteComplexLink(
    id,
    "tirageInExhibition",
    type,
  );
  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteTirageInExhibition({ id, relation: "tirageInExhibition" });
    },
    [deleteTirageInExhibition],
  );

  const renderTirageInExhibitionRowActions = useCallback(
    (data: TirageInExhibition) => {
      const preview =
        type === "exhibition"
          ? {
              previewType: "tirage" as const,
              previewId: data.tirage.id,
              previewElements: [
                {
                  label: t("tirageDescriptionNumeroEnregistrement"),
                  value: data.tirage.tirageDescriptionNumeroEnregistrement ?? "",
                  type: "text" as const,
                },
                {
                  label: t("tirageDescriptionNotes"),
                  value: data.tirage.tirageDescriptionNotes ?? "",
                  type: "richtext" as const,
                },
                {
                  label: t("attachments"),
                  value: data.tirage.attachments ?? [],
                  type: "image" as const,
                },
              ],
            }
          : type === "tirage"
            ? {
                previewType: "exhibition" as const,
                previewId: data.exhibition.id,
                previewElements: [
                  {
                    label: t("expositionsDescriptionNumeroExpo"),
                    value: data.exhibition.expositionsDescriptionNumeroExpo,
                    type: "text" as const,
                  },
                  {
                    label: t("expositionsDescriptionResume"),
                    value: data.exhibition.expositionsDescriptionResume,
                    type: "richtext" as const,
                  },
                ],
              }
            : null;

      if (!preview) return null;

      return (
        <LinkDrawerRowActions
          previewType={preview.previewType}
          previewId={preview.previewId}
          previewElements={preview.previewElements}
          onDelete={() => handleDeleteRelation(data.id)}
          editDrawer={({ open, onOpenChange }) =>
            type === "tirage" ? (
              <ComplexItinerantGate
                open={open}
                onOpenChange={onOpenChange}
                parentType="tirage"
                parentId={id}
                exhibition={data.exhibition}
                existingLink={data}
                allLinks={complexeRelation}
                complexRelationKey="tirageInExhibition"
                getCatalogNumber={(l) =>
                  l.tirageDansExpositionsDescriptionNumeroCatalogue
                }
                classic={
                  <TirageInExhibitionDrawer
                    fields={fields}
                    data={data}
                    queryId={id}
                    type={type}
                    open={open}
                    onOpenChange={onOpenChange}
                  />
                }
              />
            ) : (
              <TirageInExhibitionDrawer
                fields={fields}
                data={data}
                queryId={id}
                type={type}
                open={open}
                onOpenChange={onOpenChange}
              />
            )
          }
        />
      );
    },
    [fields, handleDeleteRelation, id, t, type, complexeRelation],
  );

  const columns: ColumnDef<TirageInExhibition, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? [
            {
              accessorKey: "expositionsDescriptionNumeroExpo",
              header: t("tab_expositionsDescriptionNumeroExpo"),
              enableResizing: true,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={
                      row.original.exhibition.expositionsDescriptionNumeroExpo
                    }
                  />
                );
              },
            },
            {
              accessorKey: "tirageInExhibitionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <ImageCell
                    value={""}
                    attachments={
                      row.original.exhibition?.attachments ??
                      row.original.attachments ??
                      []
                    }
                    domain={"exhibition"}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionAnnee",
              header: t("tab_expositionsDescriptionAnnee"),
              size: 10,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionAnnee}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionTitre",
              header: t("tab_expositionsDescriptionTitre"),
              size: 300,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <HtmlCell
                    value={row.original.exhibition.expositionsDescriptionTitre}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionDates",
              header: t("tab_expositionsDescriptionDates"),
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <TextCell
                    value={row.original.exhibition.expositionsDescriptionDates}
                  />
                );
              },
            },
            {
              accessorKey: "expositionsDescriptionLieu",
              header: t("tab_expositionsDescriptionLieu"),
              size: 300,
              cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                return (
                  <div className="flex flex-col items-start text-wrap">
                    <p className="text-[14px] font-[500]">
                      {row.original.exhibition.expositionsDescriptionVille}
                    </p>
                    <p className="text-[14px] font-[400] text-gray-500">
                      {row.original.exhibition.expositionsDescriptionLieu}
                    </p>
                  </div>
                );
              },
            },
          ]
        : type === "exhibition"
          ? [
              {
                accessorKey: "tirage.tirageDescriptionNumeroEnregistrement",
                header: t("tirageDescriptionNumeroEnregistrement"),
                enableResizing: true,
                cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                  return (
                    <TextCell
                      value={
                        row.original.tirage
                          .tirageDescriptionNumeroEnregistrement ?? ""
                      }
                    />
                  );
                },
              },
              {
                accessorKey: "tirageInExhibitionVignette",
                header: "",
                size: 100,
                cell: ({ row }: { row: Row<TirageInExhibition> }) => {
                  return (
                    <ImageCell
                      value={""}
                      attachments={
                        row.original.tirage?.attachments ??
                        row.original.attachments ??
                        []
                      }
                      domain={"tirage"}
                    />
                  );
                },
              },
            ]
          : []),
      {
        accessorKey: "tirageDansExpositionsDescriptionNumeroCatalogue",
        header: t("tirageDansExpositionsDescriptionNumeroCatalogue"),
      },
      {
        accessorKey: "tirageDansExpositionsNotes",
        header: t("tirageDansExpositionsNotes"),
        cell: ({ row }: { row: Row<TirageInExhibition> }) => {
          return (
            <HtmlCell value={row.original.tirageDansExpositionsNotes ?? ""} />
          );
        },
      },
      createActionsColumnDef<TirageInExhibition>(({ row }) =>
        renderTirageInExhibitionRowActions(row.original),
      ),
    ];
  }, [renderTirageInExhibitionRowActions, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "exhibition") {
      return (
        <ExhibitionToTirage
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("tiragesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    } else {
      return (
        <TirageToExhibition
          id={id}
          relatedData={complexeRelation}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("exhibitionsRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
  }, [
    type,
    id,
    complexeRelation,
    fields,
    exhibition,
    isAddMode,
    columns,
    searchContent,
    tirage,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

export default TirageInExhibitions;

type ExhibitionToTirageProps = LinksTableProps<TirageInExhibition> & {
  exhibition: Exhibition | undefined;
};

// Tirages liés à une exposition
const ExhibitionToTirage = ({
  id,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ExhibitionToTirageProps) => {
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "tirage-links-exhibition-add";
  const STORAGE_KEY_LINKED = "tirage-links-exhibition-linked";
  const defaultVisibility = { ...tiragesColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const { t } = useTranslation();

  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.tirageDescriptionNumeroEnregistrement ?? ""}
            />
          );
        },
      },
      {
        accessorKey: "tirageDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Tirage> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="tirage"
          />
        ),
      },
      createActionsColumnDef<Tirage>(({ row }: { row: Row<Tirage> }) => {
          if (!exhibition) {
            return null;
          }
          return (
            <LinkDrawerRowActions
              previewType="tirage"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("tirageDescriptionNumeroEnregistrement"),
                  value: row.original.tirageDescriptionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("tirageDescriptionNotes"),
                  value: row.original.tirageDescriptionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={exhibition.id}
                  type={"exhibition"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    tirage: row.original,
                    exhibition: exhibition,
                    tirageDansExpositionsDescriptionNumeroCatalogue: "",
                    tirageDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [exhibition, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsTirages}
      data={notAlreadyRelated?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelated?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <TirageLinkGridCard
                    tirage={row.tirage}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <TirageInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="exhibition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

type TirageToExhibitionProps = LinksTableProps<TirageInExhibition> & {
  tirage: Tirage | undefined;
};

// Expositions liées à un tirage
const TirageToExhibition = ({
  id,
  relatedData,
  fields,
  tirage,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: TirageToExhibitionProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_EXH = "exhibition-links-tirage-add";
  const STORAGE_KEY_LINKED_EXH = "exhibition-links-tirage-linked";
  const defaultVisibilityExh = {
    ...exhibitionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAddExh, setColumnVisibilityAddExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnVisibilityLinkedExh, setColumnVisibilityLinkedExh] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityExh, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityExh };
    });
  const [columnsOrderAddExh, setColumnsOrderAddExh] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_EXH);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedExh, setColumnsOrderLinkedExh] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_EXH);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelated } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("tab_expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            row.original.attachments && (
              <ImageCell
                value={"expositionsDescriptionVignette"}
                attachments={row.original.attachments}
                domain={"exhibition"}
              />
            )
          );
        },
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("tab_expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionAnnee} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("tab_expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.expositionsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("tab_expositionsDescriptionDates"),
        cell: ({ row }) => {
          return <TextCell value={row.original.expositionsDescriptionDates} />;
        },
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("tab_expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.expositionsDescriptionVille}
              </p>
              <p className="text-[14px] font-[400] text-gray-500">
                {row.original.expositionsDescriptionLieu}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Exhibition>(({ row }) => {
          if (!tirage) {
            return null;
          }
          return (
            <LinkDrawerRowActions
              previewType="exhibition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("expositionsDescriptionNumeroExpo"),
                  value: row.original.expositionsDescriptionNumeroExpo,
                  type: "text",
                },
                {
                  label: t("expositionsDescriptionResume"),
                  value: row.original.expositionsDescriptionResume,
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ComplexItinerantGate
                  key={row.original.id}
                  open={open}
                  onOpenChange={onOpenChange}
                  parentType="tirage"
                  parentId={tirage.id}
                  exhibition={row.original}
                  allLinks={relatedData}
                  complexRelationKey="tirageInExhibition"
                  getCatalogNumber={(l) =>
                    l.tirageDansExpositionsDescriptionNumeroCatalogue
                  }
                  classic={
                    <TirageInExhibitionDrawer
                      fields={fields}
                      queryId={tirage.id}
                      type={"tirage"}
                      open={open}
                      onOpenChange={onOpenChange}
                      data={{
                        id: "",
                        tirage: tirage,
                        exhibition: row.original,
                        tirageDansExpositionsDescriptionNumeroCatalogue: "",
                        tirageDansExpositionsNotes: "",
                      }}
                    />
                  }
                />
              )}
            />
          );
        }),
    ];
  }, [tirage, fields, t, relatedData]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsExhibitions}
      data={notAlreadyRelated?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelated?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAddExh,
        onColumnOrderChange: setColumnsOrderAddExh,
        state: {
          columnVisibility: columnVisibilityAddExh,
          columnOrder: columnsOrderAddExh,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_EXH}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinkedExh,
        onColumnOrderChange: setColumnsOrderLinkedExh,
        state: {
          columnVisibility: columnVisibilityLinkedExh,
          columnOrder: columnsOrderLinkedExh,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_EXH}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ExhibitionLinkGridCard
                    item={row.exhibition}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <ComplexItinerantGate
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                  parentType="tirage"
                  parentId={id}
                  exhibition={row.exhibition}
                  existingLink={row}
                  allLinks={relatedData}
                  complexRelationKey="tirageInExhibition"
                  getCatalogNumber={(l) =>
                    l.tirageDansExpositionsDescriptionNumeroCatalogue
                  }
                  classic={
                    <TirageInExhibitionDrawer
                      fields={fields}
                      data={row}
                      queryId={id}
                      type="tirage"
                      open={selectedRelationId === row.id}
                      onOpenChange={(open) => {
                        if (!open) setSelectedRelationId(null);
                      }}
                    />
                  }
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};
